import { useLogEvent } from 'api/services/LogEventService';
import {
  Search as CallSearch,
  SuppliersSearch as callSuppliersSearch
} from 'api/services/SearchService';
import { AxiosResponse } from 'axios';
import { useCallback, useMemo, useState } from 'react';
import { FetchResponse } from 'types/global';
import { LogEventType } from 'types/LogEvents';
import { ProductType } from 'types/Products';
import { SearchRequest, SearchResults, SearchSource } from 'types/Search';
import {
  CustomAttributesTypes,
  UnionCustomAttributeProps
} from '../Portals/CustomAttributes/types';
import { formatSearchLog } from 'helpers/utilsTS';
import { useParams } from 'react-router-dom';

export const useSearch = (
  companyAttributesDefinitions: UnionCustomAttributeProps[] | undefined,
  productAttributesDefinitions: UnionCustomAttributeProps[] | undefined
) => {
  const [loading, setLoading] = useState(false);
  const { event: eventKey } = useParams();
  const logSearchSuccess = useLogSearchSuccess();
  const keysOfAdditionalOrFilters = useMemo(
    () =>
      getOrFiltersFromDefinitions([
        ...(companyAttributesDefinitions ?? []),
        ...(productAttributesDefinitions ?? [])
      ]),
    [companyAttributesDefinitions, productAttributesDefinitions]
  );

  const executeSearch = useCallback(
    (
      searchData: SearchRequest,
      selectedSearchSource: SearchSource,
      success: (
        response: AxiosResponse<SearchResults>,
        request: SearchRequest
      ) => void,
      hideLoadingIndicator: boolean
    ) => {
      if (!hideLoadingIndicator) setLoading(true);

      CallSearch(
        searchData,
        selectedSearchSource,
        response => {
          setLoading(false);
          logSearchSuccess(response, searchData, eventKey);
          success(response, searchData);
        },
        () => {
          setLoading(false);
        },
        keysOfAdditionalOrFilters
      );
    },
    [keysOfAdditionalOrFilters, eventKey, logSearchSuccess]
  );
  return { executeSearch, loading };
};

export const useSuppliersSearch = () => {
  const [loading, setLoading] = useState(false);

  const executeSearch = useCallback(
    (
      searchData: Omit<SearchRequest, 'target'>,
      selectedSearchSource: SearchSource,
      success: (
        response: AxiosResponse<SearchResults>,
        request: Omit<SearchRequest, 'target'>
      ) => void,
      hideLoadingIndicator: boolean
    ) => {
      if (!hideLoadingIndicator) setLoading(true);

      callSuppliersSearch(
        searchData,
        selectedSearchSource,
        response => {
          setLoading(false);
          success(response, searchData);
        },
        () => {
          setLoading(false);
        }
      );
    },
    []
  );
  return { executeSearch, loading };
};

const useLogSearchSuccess = () => {
  const logEvent = useLogEvent();

  const logSearchSuccess = useCallback(
    (response: FetchResponse, request: SearchRequest, eventKey?: string) => {
      const logBody = formatSearchLog(
        response.data,
        request.filters,
        request.segment
      );

      const eventType =
        request.target === ProductType.INGREDIENTS
          ? LogEventType.PRODUCT_IMPRESSION
          : request.target === ProductType.FORMULATIONS
          ? LogEventType.FORMULATION_IMPRESSION
          : LogEventType.SERVICE_IMPRESSION;

      logEvent(logBody, eventType, eventKey);
    },
    [logEvent]
  );
  return logSearchSuccess;
};
const getOrFiltersFromDefinitions = (
  definitions: UnionCustomAttributeProps[]
) =>
  definitions
    .filter(
      d =>
        d.type === CustomAttributesTypes.SINGLE_SELECT ||
        d.type === CustomAttributesTypes.MULTI_SELECT
    )
    .map(d => d.key);
